'use client'

import { useState, useEffect, useCallback } from 'react'
import { toast } from 'sonner'
import { useTranslations } from 'next-intl'
import {
  Users,
  UserPlus,
  Crown,
  Shield,
  Edit3,
  Eye,
  MoreHorizontal,
  Trash2,
  Mail,
  Link2,
  Copy,
  Check,
  Loader2,
  ArrowRightLeft,
  Sparkles,
  Clock,
  X,
  LogOut,
} from 'lucide-react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { pushModal } from '@/components/modals'
import { useWorkspaceSubscription } from '@/hooks/subscriptions/use-workspace-subscription'

interface Member {
  _id: string
  user_id: string
  role: string
  is_owner: boolean
  display_role: string
  user: {
    _id: string
    email: string
    name: string
    avatar?: string
  } | null
  joined_at: string
}

interface PendingInvitation {
  _id: string
  email: string
  role: string
  display_role: string
  expires_at: string
  inviter: {
    email: string
    name: string
  } | null
}

interface MembersManagerProps {
  workspaceId: string
  workspaceName: string
  currentUserId?: string
  currentUserRole?: string
  currentUserIsOwner?: boolean
}

// Role config will be created inside component to use translations

export default function MembersManager({
  workspaceId,
  workspaceName,
  currentUserId,
  currentUserRole = 'viewer',
  currentUserIsOwner = false,
}: MembersManagerProps) {
  const t = useTranslations('workspace.members')
  const [members, setMembers] = useState<Member[]>([])
  const [pendingInvitations, setPendingInvitations] = useState<
    PendingInvitation[]
  >([])
  const [loading, setLoading] = useState(true)
  const [owner, setOwner] = useState<any>(null)
  const [copiedLink, setCopiedLink] = useState(false)
  const [quickInviteEmail, setQuickInviteEmail] = useState('')
  const [quickInviteRole, setQuickInviteRole] = useState('member')
  const [isQuickInviting, setIsQuickInviting] = useState(false)

  // Get workspace subscription to check plan
  const { activeSubscription } = useWorkspaceSubscription(workspaceId)
  const workspacePlan =
    activeSubscription && activeSubscription.status === 'active'
      ? activeSubscription.plan
      : 'free'
  const hasPremiumOrPlatinum =
    workspacePlan === 'premium' || workspacePlan === 'platinum'

  // Role config with translations
  const ROLE_CONFIG: Record<
    string,
    {
      icon: React.ReactNode
      color: string
      bg: string
      border: string
      description: string
    }
  > = {
    Owner: {
      icon: <Crown className="w-3.5 h-3.5" />,
      color: 'text-gold',
      bg: 'bg-gold/10',
      border: 'border-gold/30',
      description: t('roles.owner.description'),
    },
    Admin: {
      icon: <Shield className="w-3.5 h-3.5" />,
      color: 'text-blue-400',
      bg: 'bg-blue-500/10',
      border: 'border-blue-500/30',
      description: t('roles.admin.description'),
    },
    Editor: {
      icon: <Edit3 className="w-3.5 h-3.5" />,
      color: 'text-emerald-400',
      bg: 'bg-emerald-500/10',
      border: 'border-emerald-500/30',
      description: t('roles.editor.description'),
    },
    Viewer: {
      icon: <Eye className="w-3.5 h-3.5" />,
      color: 'text-white/60',
      bg: 'bg-white/5',
      border: 'border-white/20',
      description: t('roles.viewer.description'),
    },
  }
  const [actualCurrentUserId, setActualCurrentUserId] = useState<
    string | undefined
  >(currentUserId)

  const canManageMembers = currentUserRole === 'admin' || currentUserIsOwner
  // Only admin and owner can invite members (editor/member cannot invite)
  const canInvite = currentUserRole === 'admin' || currentUserIsOwner

  const fetchMembers = useCallback(async () => {
    try {
      setLoading(true)
      const res = await fetch(`/api/me/workspaces/${workspaceId}/members`)
      const data = await res.json()

      if (data.success) {
        // Build full members list with owner first
        const ownerData = data.data.owner
        const membersList = data.data.members || []

        // Check if owner is already in the members list
        const ownerInMembers = membersList.some(
          (m: Member) => m.user_id === ownerData?._id?.toString(),
        )

        // Create owner member entry if not in list
        if (ownerData && !ownerInMembers) {
          const ownerMember: Member = {
            _id: 'owner-' + ownerData._id,
            user_id: ownerData._id?.toString() || '',
            role: 'owner',
            is_owner: true,
            display_role: 'Owner',
            user: {
              _id: ownerData._id,
              email: ownerData.email,
              name: ownerData.name,
              avatar: ownerData.avatar,
            },
            joined_at: new Date().toISOString(),
          }
          // Put owner first
          setMembers([ownerMember, ...membersList])
        } else {
          // Mark the owner in the existing list
          const membersWithOwner = membersList.map((m: Member) => ({
            ...m,
            is_owner: m.user_id === ownerData?._id?.toString(),
            display_role:
              m.user_id === ownerData?._id?.toString()
                ? 'Owner'
                : m.display_role,
          }))
          // Sort to put owner first
          membersWithOwner.sort((a: Member, b: Member) => {
            if (a.is_owner) return -1
            if (b.is_owner) return 1
            return 0
          })
          setMembers(membersWithOwner)
        }

        setPendingInvitations(data.data.pending_invitations || [])
        setOwner(ownerData)

        // Get current user ID from the response if available
        if (data.data.current_user_id) {
          setActualCurrentUserId(data.data.current_user_id)
        }
      }
    } catch (error) {
      console.error('Failed to fetch members:', error)
      toast.error(t('errors.failed_to_load_members'))
    } finally {
      setLoading(false)
    }
  }, [workspaceId])

  useEffect(() => {
    fetchMembers()
  }, [fetchMembers])

  const handleRemoveMember = async (memberId: string, memberName: string) => {
    if (!confirm(t('confirm.remove_member', { memberName }))) {
      return
    }

    try {
      const res = await fetch(
        `/api/me/workspaces/${workspaceId}/members/${memberId}`,
        { method: 'DELETE' },
      )
      const data = await res.json()

      if (data.success) {
        toast.success(t('success.member_removed'))
        fetchMembers()
      } else {
        toast.error(data.message || t('errors.failed_to_remove_member'))
      }
    } catch (error) {
      toast.error(t('errors.failed_to_remove_member'))
    }
  }

  const handleLeaveWorkspace = async (memberId: string) => {
    if (!confirm(t('confirm.leave_workspace', { workspaceName }))) {
      return
    }

    try {
      const res = await fetch(
        `/api/me/workspaces/${workspaceId}/members/${memberId}`,
        { method: 'DELETE' },
      )
      const data = await res.json()

      if (data.success) {
        toast.success(t('success.left_workspace'))
        // Redirect to homepage after leaving
        window.location.href = '/'
      } else {
        toast.error(data.message || t('errors.failed_to_leave_workspace'))
      }
    } catch (error) {
      toast.error(t('errors.failed_to_leave_workspace'))
    }
  }

  const handleRoleChange = async (memberId: string, newRole: string) => {
    // Check subscription for Admin and Viewer roles
    if (
      (newRole === 'admin' || newRole === 'viewer') &&
      !hasPremiumOrPlatinum
    ) {
      pushModal('PricingModal', {
        type: 'need_premium',
        workspaceId,
        minRequiredPlan: 'premium',
        trigger: 'workspace-member-management',
      })
      return
    }

    try {
      const res = await fetch(
        `/api/me/workspaces/${workspaceId}/members/${memberId}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ role: newRole }),
        },
      )
      const data = await res.json()

      if (data.success) {
        toast.success(t('success.role_updated'))
        fetchMembers()
      } else {
        toast.error(data.message || t('errors.failed_to_update_role'))
      }
    } catch (error) {
      toast.error(t('errors.failed_to_update_role'))
    }
  }

  const handleCancelInvitation = async (invitationId: string) => {
    try {
      const res = await fetch(
        `/api/me/workspaces/${workspaceId}/members/invite`,
        {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ invitation_id: invitationId }),
        },
      )
      const data = await res.json()

      if (data.success) {
        toast.success(t('success.invitation_cancelled'))
        fetchMembers()
      } else {
        toast.error(data.message || t('errors.failed_to_cancel_invitation'))
      }
    } catch (error) {
      toast.error(t('errors.failed_to_cancel_invitation'))
    }
  }

  const [inviteLinkRole, setInviteLinkRole] = useState<'editor' | 'viewer'>(
    'editor',
  )

  const handleCopyInviteLink = async () => {
    // Check subscription for Viewer role
    if (inviteLinkRole === 'viewer' && !hasPremiumOrPlatinum) {
      pushModal('PricingModal', {
        type: 'need_premium',
        workspaceId,
        minRequiredPlan: 'premium',
        trigger: 'workspace-member-management',
      })
      return
    }

    try {
      const res = await fetch(`/api/me/workspaces/${workspaceId}/invite-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ default_role: inviteLinkRole }),
      })
      const data = await res.json()

      if (data.success && data.data.link) {
        await navigator.clipboard.writeText(data.data.link)
        setCopiedLink(true)
        toast.success(t('success.invite_link_copied'))
        setTimeout(() => setCopiedLink(false), 2000)
      } else {
        toast.error(t('errors.failed_to_generate_link'))
      }
    } catch (error) {
      toast.error(t('errors.failed_to_copy_link'))
    }
  }

  const handleQuickInvite = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!quickInviteEmail) return

    // Check subscription for Admin and Viewer roles (Editor is free)
    if (
      (quickInviteRole === 'admin' || quickInviteRole === 'viewer') &&
      !hasPremiumOrPlatinum
    ) {
      // Show pricing modal to upgrade
      pushModal('PricingModal', {
        type: 'need_premium',
        workspaceId,
        minRequiredPlan: 'premium',
        trigger: 'workspace-member-management',
      })
      return
    }

    setIsQuickInviting(true)
    try {
      const res = await fetch(
        `/api/me/workspaces/${workspaceId}/members/invite`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: quickInviteEmail,
            role: quickInviteRole,
          }),
        },
      )
      const data = await res.json()

      if (data.success) {
        toast.success(t('success.invitation_sent', { email: quickInviteEmail }))
        setQuickInviteEmail('')
        fetchMembers()
      } else {
        toast.error(data.message || t('errors.failed_to_send_invitation'))
      }
    } catch (error) {
      toast.error(t('errors.failed_to_send_invitation'))
    } finally {
      setIsQuickInviting(false)
    }
  }

  const openTransferModal = () => {
    pushModal('TransferOwnershipModal', {
      workspaceId,
      workspaceName,
      members: members.filter((m) => !m.is_owner),
      onSuccess: fetchMembers,
    })
  }

  const getRoleConfig = (role: string) => {
    return ROLE_CONFIG[role] || ROLE_CONFIG.Viewer
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-4">
        <div className="relative">
          <div className="w-12 h-12 rounded-full border-2 border-gold/20 border-t-gold animate-spin" />
          <Sparkles className="w-5 h-5 text-gold absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
        </div>
        <p className="text-white/50 text-sm">{t('loading')}</p>
      </div>
    )
  }

  return (
    <TooltipProvider>
      <div className="space-y-8">
        {/* Quick Invite Section - Lovable style inline input */}
        {canInvite && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-full bg-gold/10 flex items-center justify-center">
                <UserPlus className="w-4 h-4 text-gold" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-white">
                  {t('invite_team_members')}
                </h3>
                <p className="text-xs text-white/50">
                  {t('invite_description')}
                </p>
              </div>
            </div>

            <form onSubmit={handleQuickInvite} className="flex gap-2">
              <div className="flex-1 relative">
                <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                <Input
                  type="email"
                  placeholder={t('email_placeholder')}
                  value={quickInviteEmail}
                  onChange={(e) => setQuickInviteEmail(e.target.value)}
                  className={cn(
                    'pl-10 h-11',
                    'bg-[#0a0a0a] border-gold/20',
                    'text-white placeholder:text-white/30',
                    'focus:border-gold/50 focus:ring-gold/20',
                    'transition-all duration-200',
                  )}
                />
              </div>

              <Select
                value={quickInviteRole}
                onValueChange={setQuickInviteRole}
              >
                <SelectTrigger
                  className={cn(
                    'w-[110px] h-11',
                    'bg-[#0a0a0a] border-gold/20',
                    'text-white',
                    'focus:border-gold/50 focus:ring-gold/20',
                  )}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1a1a1a] border-gold/20">
                  <SelectItem
                    value="member"
                    className="text-white hover:bg-gold/10"
                  >
                    {t('roles.editor.label')}
                  </SelectItem>
                  <SelectItem
                    value="viewer"
                    className="text-white hover:bg-gold/10"
                  >
                    <div className="flex items-center gap-1.5">
                      {t('roles.viewer.label')}
                      <Crown className="w-3 h-3 text-gold" />
                    </div>
                  </SelectItem>
                  {canManageMembers && (
                    <SelectItem
                      value="admin"
                      className="text-white hover:bg-gold/10"
                    >
                      <div className="flex items-center gap-1.5">
                        {t('roles.admin.label')}
                        <Crown className="w-3 h-3 text-gold" />
                      </div>
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>

              <Button
                type="submit"
                disabled={!quickInviteEmail || isQuickInviting}
                className={cn(
                  'h-11 px-5',
                  'bg-gold hover:bg-gold/90',
                  'text-black font-semibold',
                  'transition-all duration-200',
                  'hover:scale-[1.02] active:scale-[0.98]',
                  'disabled:opacity-50 disabled:hover:scale-100',
                )}
              >
                {isQuickInviting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  t('invite_button')
                )}
              </Button>
            </form>

            {/* Copy Link Button */}
            {canManageMembers && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <label className="text-xs text-white/60 flex-shrink-0">
                    {t('invite_as')}:
                  </label>
                  <Select
                    value={inviteLinkRole}
                    onValueChange={(value: 'editor' | 'viewer') => {
                      // Check subscription when selecting viewer
                      if (value === 'viewer' && !hasPremiumOrPlatinum) {
                        pushModal('PricingModal', {
                          type: 'need_premium',
                          workspaceId,
                          minRequiredPlan: 'premium',
                          trigger: 'workspace-member-management',
                        })
                        // Don't change the role if subscription check fails
                        return
                      }
                      setInviteLinkRole(value)
                    }}
                  >
                    <SelectTrigger className="h-8 text-xs bg-[#1a1a1a] border-gold/20 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1a1a1a] border-gold/20">
                      <SelectItem value="editor">
                        {t('roles.editor.label')}
                      </SelectItem>
                      <SelectItem value="viewer">
                        <div className="flex items-center gap-1.5">
                          {t('roles.viewer.label')}
                          <Crown className="w-3 h-3 text-gold" />
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <button
                  onClick={handleCopyInviteLink}
                  className={cn(
                    'w-full py-2.5 px-4 rounded-lg',
                    'border border-dashed border-gold/20',
                    'bg-gold/5 hover:bg-gold/10',
                    'text-white/70 hover:text-white',
                    'flex items-center justify-center gap-2',
                    'transition-all duration-200',
                    'text-sm',
                  )}
                >
                  {copiedLink ? (
                    <>
                      <Check className="w-4 h-4 text-emerald-400" />
                      <span className="text-emerald-400">
                        {t('link_copied')}
                      </span>
                    </>
                  ) : (
                    <>
                      <Link2 className="w-4 h-4" />
                      <span>{t('copy_invite_link')}</span>
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Members List */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-gold" />
              <h3 className="text-sm font-semibold text-white">
                {t('team_members')}
              </h3>
              <span className="text-xs text-white/40 ml-1">
                ({members.length})
              </span>
            </div>
          </div>

          <div className="space-y-1">
            {members.map((member, index) => {
              const roleConfig = getRoleConfig(member.display_role)
              const isCurrentUser = actualCurrentUserId
                ? member.user_id === actualCurrentUserId
                : false

              return (
                <div
                  key={member._id}
                  className={cn(
                    'group flex items-center justify-between',
                    'p-3 rounded-xl',
                    'bg-[#0a0a0a]/50 hover:bg-[#0a0a0a]',
                    'border border-transparent hover:border-gold/10',
                    'transition-all duration-200',
                    index === 0 && 'bg-gold/5 border-gold/20', // Highlight owner
                  )}
                >
                  <div className="flex items-center gap-3">
                    {/* Avatar with role indicator */}
                    <div className="relative">
                      <Avatar className="w-10 h-10 ring-2 ring-transparent group-hover:ring-gold/20 transition-all">
                        <AvatarImage src={member.user?.avatar} />
                        <AvatarFallback
                          className={cn(
                            'font-semibold text-sm',
                            member.is_owner
                              ? 'bg-gold/20 text-gold'
                              : 'bg-white/10 text-white/70',
                          )}
                        >
                          {member.user?.name?.charAt(0) ||
                            member.user?.email?.charAt(0) ||
                            '?'}
                        </AvatarFallback>
                      </Avatar>
                      {member.is_owner && (
                        <div
                          className={cn(
                            'absolute -bottom-0.5 -right-0.5',
                            'w-4 h-4 rounded-full',
                            'bg-gold flex items-center justify-center',
                            'ring-2 ring-[#1a1a1a]',
                          )}
                        >
                          <Crown className="w-2.5 h-2.5 text-black" />
                        </div>
                      )}
                    </div>

                    {/* Name and Email */}
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-white truncate">
                          {member.user?.name ||
                            member.user?.email?.split('@')[0] ||
                            'Unknown'}
                        </span>
                        {isCurrentUser && (
                          <Badge
                            variant="outline"
                            className="text-[10px] px-1.5 py-0 h-4 border-gold/30 text-gold/70"
                          >
                            {t('you')}
                          </Badge>
                        )}
                      </div>
                      <span className="text-xs text-white/40 truncate block">
                        {member.user?.email}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {/* Role Badge/Selector */}
                    {canManageMembers && !member.is_owner ? (
                      <Select
                        value={member.role}
                        onValueChange={(value) =>
                          handleRoleChange(member._id, value)
                        }
                      >
                        <SelectTrigger
                          className={cn(
                            'w-[100px] h-8 text-xs font-medium',
                            'border',
                            roleConfig.bg,
                            roleConfig.border,
                            roleConfig.color,
                            'hover:opacity-90 transition-opacity',
                          )}
                        >
                          <div className="flex items-center gap-1.5">
                            <SelectValue />
                          </div>
                        </SelectTrigger>
                        <SelectContent className="bg-[#1a1a1a] border-gold/20">
                          <SelectItem
                            value="admin"
                            className="text-white hover:bg-gold/10"
                          >
                            <div className="flex items-center justify-between w-full">
                              <div className="flex items-center gap-2">
                                <Shield className="w-3.5 h-3.5 text-blue-400" />
                                {t('roles.admin.label')}
                              </div>
                              <Crown className="w-3 h-3 text-gold flex-shrink-0" />
                            </div>
                          </SelectItem>
                          <SelectItem
                            value="member"
                            className="text-white hover:bg-gold/10"
                          >
                            <div className="flex items-center gap-2">
                              <Edit3 className="w-3.5 h-3.5 text-emerald-400" />
                              {t('roles.editor.label')}
                            </div>
                          </SelectItem>
                          <SelectItem
                            value="viewer"
                            className="text-white hover:bg-gold/10"
                          >
                            <div className="flex items-center justify-between w-full">
                              <div className="flex items-center gap-2">
                                <Eye className="w-3.5 h-3.5 text-white/60" />
                                {t('roles.viewer.label')}
                              </div>
                              <Crown className="w-3 h-3 text-gold flex-shrink-0" />
                            </div>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <Tooltip>
                        <TooltipTrigger>
                          <Badge
                            variant="outline"
                            className={cn(
                              'flex items-center gap-1.5 px-2.5 py-1',
                              'text-xs font-medium',
                              roleConfig.bg,
                              roleConfig.border,
                              roleConfig.color,
                            )}
                          >
                            {roleConfig.icon}
                            {member.display_role}
                          </Badge>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>{roleConfig.description}</p>
                        </TooltipContent>
                      </Tooltip>
                    )}

                    {/* Actions */}
                    {/* Show actions for: admins managing others, OR current user leaving (if not owner) */}
                    {!member.is_owner &&
                      (canManageMembers || isCurrentUser) && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className={cn(
                                'h-8 w-8',
                                'text-white/40 hover:text-white',
                                'opacity-0 group-hover:opacity-100',
                                'transition-opacity',
                              )}
                            >
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent
                            align="end"
                            className="bg-[#1a1a1a] border-gold/20"
                          >
                            {isCurrentUser ? (
                              <DropdownMenuItem
                                onClick={() => handleLeaveWorkspace(member._id)}
                                className="text-red-400 focus:text-red-400 focus:bg-red-500/10"
                              >
                                <LogOut className="w-4 h-4 mr-2" />
                                {t('leave_workspace')}
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem
                                onClick={() =>
                                  handleRemoveMember(
                                    member._id,
                                    member.user?.name ||
                                      member.user?.email ||
                                      'Member',
                                  )
                                }
                                className="text-red-400 focus:text-red-400 focus:bg-red-500/10"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                {t('remove_from_workspace')}
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* Pending Invitations */}
        {pendingInvitations.length > 0 && canManageMembers && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-yellow-500" />
              <h3 className="text-sm font-semibold text-white">
                {t('pending_invitations')}
              </h3>
              <span className="text-xs text-white/40 ml-1">
                ({pendingInvitations.length})
              </span>
            </div>

            <div className="space-y-1">
              {pendingInvitations.map((invitation) => {
                const roleConfig = getRoleConfig(invitation.display_role)
                const expiresAt = new Date(invitation.expires_at)
                const daysLeft = Math.ceil(
                  (expiresAt.getTime() - Date.now()) / (1000 * 60 * 60 * 24),
                )

                return (
                  <div
                    key={invitation._id}
                    className={cn(
                      'group flex items-center justify-between',
                      'p-3 rounded-xl',
                      'bg-yellow-500/5',
                      'border border-dashed border-yellow-500/20',
                      'hover:border-yellow-500/30',
                      'transition-all duration-200',
                    )}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-yellow-500/10 flex items-center justify-center">
                        <Mail className="w-5 h-5 text-yellow-500/60" />
                      </div>

                      <div className="min-w-0">
                        <span className="font-medium text-white/80 truncate block">
                          {invitation.email}
                        </span>
                        <div className="flex items-center gap-2 text-xs text-white/40">
                          <span>
                            {t('invited_as', { role: invitation.display_role })}
                          </span>
                          <span>•</span>
                          <span>
                            {daysLeft > 0
                              ? t('expires_in_days', { count: daysLeft })
                              : t('expires_today')}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <Badge
                        variant="outline"
                        className="bg-yellow-500/10 text-yellow-500 border-yellow-500/30 text-xs"
                      >
                        {t('pending')}
                      </Badge>

                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleCancelInvitation(invitation._id)}
                        className={cn(
                          'h-8 w-8',
                          'text-white/40 hover:text-red-400',
                          'hover:bg-red-500/10',
                          'opacity-0 group-hover:opacity-100',
                          'transition-all',
                        )}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* Transfer Ownership - Bottom action */}
        {currentUserIsOwner && members.length > 1 && (
          <div className="pt-6 border-t border-gold/10">
            <button
              onClick={openTransferModal}
              className={cn(
                'w-full py-3 px-4 rounded-xl',
                'border border-gold/20',
                'bg-transparent hover:bg-gold/5',
                'text-white/70 hover:text-white',
                'flex items-center justify-center gap-2',
                'transition-all duration-200',
                'group',
              )}
            >
              <ArrowRightLeft className="w-4 h-4 group-hover:text-gold transition-colors" />
              <span>{t('transfer_ownership')}</span>
            </button>
            <p className="text-xs text-white/30 text-center mt-2">
              {t('transfer_ownership_description')}
            </p>
          </div>
        )}
      </div>
    </TooltipProvider>
  )
}
